import React from 'react';
import DIAGRAM_SIZES from './constants/DiagramNumbers';
import styled from 'styled-components';
import NodeArrows from './NodeArrows';

const {COLUMN_GAP_PX, ARROW_SIZE_PX, MIN_COLUMNS} = DIAGRAM_SIZES;

const StyledPathway = styled.div`
  grid-area: pathway;
  height: 50px;
  width: 100%;
  position: relative;
`;

const StyledSVGArrow = styled.svg<{$left?: string; $top?: string; $margin?: string}>`
  position: absolute;
  left: ${props => props.$left || 'auto'};
  top: ${props => props.$top || 'auto'};

  polygon {
    fill: ${props => props.theme.text};
  }
  &.up {
    transform: rotate(90deg);
    margin-left: ${props => props.$margin || '-5px'};
  }
  &.down {
    transform: rotate(90deg);
    bottom: -5px;
    margin-left: -5px;
  }
`;

const StyledSVGLines = styled.svg`
  position: absolute;
  line {
    stroke: ${props => props.theme.text};
  }
  color: ${props => props.theme.text};
`;

// region calculatePointerPosition
function calculatePointerPosition(columnIndex: number, totalColumns: number) {
  const BASE_HORIZONTAL_LINE = `80%`;
  const ARROW_MIDPOINT = DIAGRAM_SIZES.ARROW_SIZE_PX / 2;

  // calculated down arrow position
  const totalCalculatedColumns = Math.max(totalColumns, MIN_COLUMNS);
  const totalGapPx = (totalCalculatedColumns - 1) * COLUMN_GAP_PX;

  const columnWidth = `calc((100% - ${totalGapPx}px) / ${totalCalculatedColumns})`;
  const gapWidth = `calc(${COLUMN_GAP_PX}px)`;

  const columnIndexPoint = `calc((${columnWidth} + ${gapWidth}) * ${columnIndex})`;
  const columnMidPoint = `calc(${columnWidth} / 2)`;

  const calcDownArrowPosition = `calc(${columnIndexPoint} + ${columnMidPoint})`;

  // calculated up arrow position
  const centerPosition = totalCalculatedColumns / 2;
  const adjustment = totalCalculatedColumns % 2 === 0 ? 0 : 0.5;
  const adjustedColumnIndex = centerPosition + adjustment;

  const calcNetworkDeviceArrowsPosition = `calc(((${columnWidth} + ${gapWidth}) * ${
    adjustedColumnIndex - columnIndex
  }) - ${gapWidth})`;

  const adjustedColumnIndexPoint = `calc((${columnWidth} + ${gapWidth}) * ${adjustedColumnIndex})`;

  const incrementLeftMarginPx =
    columnIndex * ARROW_SIZE_PX - totalColumns * ARROW_MIDPOINT + ARROW_MIDPOINT;
  const marginAdjustmentPx = `${incrementLeftMarginPx}px`;

  const calcUpArrowPosition = `calc(${adjustedColumnIndexPoint} + ${columnMidPoint} + ${marginAdjustmentPx})`;

  // calculated horizontal line position
  const reverseOnGroupedColumn =
    columnIndex > adjustedColumnIndex ? totalColumns - columnIndex + 1 : columnIndex;

  const incrementLineHorizonalPx =
    reverseOnGroupedColumn * ARROW_MIDPOINT - adjustedColumnIndex * ARROW_MIDPOINT;

  const calcHorizontalLinePosition = `calc(${BASE_HORIZONTAL_LINE} + ${incrementLineHorizonalPx}px)`;

  return {
    networkDeviceArrowsPosition: calcNetworkDeviceArrowsPosition,
    downArrowPosition: calcDownArrowPosition,
    upArrowPosition: calcUpArrowPosition,
    horizontalLinePosition: calcHorizontalLinePosition,
  };
}
// endregion calculatePointerPosition

// region renderPaths
function renderPaths(numberOfPaths: number) {
  const paths = [];

  for (let columnIndex = 0; columnIndex < numberOfPaths; columnIndex++) {
    const {downArrowPosition, upArrowPosition, horizontalLinePosition} = calculatePointerPosition(
      columnIndex,
      numberOfPaths
    );

    //region down-arrows
    paths.push(
      <StyledSVGArrow
        height={ARROW_SIZE_PX}
        width={ARROW_SIZE_PX}
        className="down"
        $left={downArrowPosition}
        key={'downArrow' + columnIndex}
      >
        <polygon points="0,5 5,8 5,2" strokeWidth="1" />
      </StyledSVGArrow>
    );
    //endregion down-arrows

    //region up-arrows
    paths.push(
      <StyledSVGArrow
        height={ARROW_SIZE_PX}
        width={ARROW_SIZE_PX}
        className="up"
        $left={upArrowPosition}
        key={'upArrow' + columnIndex}
      >
        <polygon points="0,5 5,8 5,2" strokeWidth="1" />
      </StyledSVGArrow>
    );
    //endregion up-arrows

    //region horizontal-line
    paths.push(
      <StyledSVGLines width="100%" height="100%" key={'line' + columnIndex}>
        <line
          x1={downArrowPosition}
          y1="100%"
          x2={downArrowPosition}
          y2={horizontalLinePosition}
          strokeWidth="1"
        />
        <line
          x1={downArrowPosition}
          y1={horizontalLinePosition}
          x2={upArrowPosition}
          y2={horizontalLinePosition}
          strokeWidth="1"
        />
        <line
          x1={upArrowPosition}
          y1={horizontalLinePosition}
          x2={upArrowPosition}
          y2={`0%`}
          strokeWidth="1"
        />
      </StyledSVGLines>
    );
  }

  return paths;
}
// endregion renderPaths

// region renderArrowNodes
function renderArrowNodes(numberOfArrowPaths: number, numberOfPaths: number) {
  const arrowNodes = [...Array(numberOfArrowPaths)].map((ele, index) => {
    const {networkDeviceArrowsPosition} = calculatePointerPosition(index, numberOfPaths);
    return (
      <NodeArrows
        top={`-100px`}
        left={`${networkDeviceArrowsPosition}`}
        width={COLUMN_GAP_PX}
        key={'nodeArrow' + index}
      />
    );
  });
  return arrowNodes;
}
// endregion renderArrowNodes

export default function PathwayArrows({numberOfPaths}: {numberOfPaths: number}) {
  return (
    <StyledPathway>
      {renderPaths(numberOfPaths)}
      {renderArrowNodes(2, numberOfPaths)}
    </StyledPathway>
  );
}
